import React from "react";
import {
  Title,
  Text,
  Card,
  Stack,
  Box,
  Grid,
  Group,
  ThemeIcon,
  Divider,
} from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import {
  IconDeviceTv,
  IconCalendar,
  IconLayout,
  IconBolt,
} from "@tabler/icons-react";

import { DashboardLayout } from "../../../components/layouts";

export const DashboardPage: React.FC<PageProps> = (props: PageProps) => {
  return (
    <DashboardLayout {...props}>
      <Stack gap="xl">
        {/* Welcome Section */}
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="md">
            <Title order={1} ta="center">
              Welcome to VenueBoards
            </Title>
            <Text size="lg" ta="center" c="dimmed">
              The integrated scheduling and digital signage platform designed
              for venues that manage shared public spaces.
            </Text>
          </Stack>
        </Card>

        {/* Key Features Grid */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Stack gap="sm" align="center">
                <ThemeIcon size="xl" radius="md" variant="light">
                  <IconCalendar size={24} />
                </ThemeIcon>
                <Text fw={600} ta="center">
                  Live Schedules
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Display real-time event schedules for your rooms and spaces
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Stack gap="sm" align="center">
                <ThemeIcon size="xl" radius="md" variant="light">
                  <IconLayout size={24} />
                </ThemeIcon>
                <Text fw={600} ta="center">
                  Dynamic Content
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Create playlists with images, videos, widgets, and
                  announcements
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Stack gap="sm" align="center">
                <ThemeIcon size="xl" radius="md" variant="light">
                  <IconDeviceTv size={24} />
                </ThemeIcon>
                <Text fw={600} ta="center">
                  Any Device
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Works on any TV or screen with a web browser
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Stack gap="sm" align="center">
                <ThemeIcon size="xl" radius="md" variant="light">
                  <IconBolt size={24} />
                </ThemeIcon>
                <Text fw={600} ta="center">
                  Real-Time Updates
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Changes appear instantly on all connected displays
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Getting Started Section */}
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="lg">
            <Title order={2}>Getting Started</Title>
            <Divider />

            <Stack gap="md">
              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    1
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Set Up Your Venue
                  </Text>
                  <Text size="sm" c="dimmed">
                    Start by creating your venue profile and defining all the
                    rooms or spaces you want to manage. For each room, specify
                    details like name, capacity, location, and type (e.g.,
                    conference room, classroom, auditorium). This foundation
                    allows you to organize and display schedules for each space.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    2
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Add Your Schedule
                  </Text>
                  <Text size="sm" c="dimmed">
                    Populate your calendar with events. You can create events
                    manually one by one, or save time by importing entire
                    schedules from .ics calendar files. Each event can be
                    assigned to specific rooms and include details like time,
                    duration, organizer, and description. This step is optional
                    if you only want to display content playlists.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    3
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Upload Your Content
                  </Text>
                  <Text size="sm" c="dimmed">
                    Build your media library by uploading images and videos that
                    you want to display. You can also create custom
                    announcements (text messages with formatting) and add
                    interactive widgets (like weather, clock, or news feeds).
                    This step is optional if you only want to show event
                    schedules.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    4
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Create Playlists
                  </Text>
                  <Text size="sm" c="dimmed">
                    Organize your uploaded content into playlists. A playlist is
                    a sequence of content items (images, videos, announcements,
                    widgets) that will rotate in a specific zone on your
                    display. You can control the duration each item shows and
                    the order they appear. Playlists are optional and only
                    needed if you want to show content alongside or instead of
                    event schedules.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    5
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Create Display Configuration
                  </Text>
                  <Text size="sm" c="dimmed">
                    Design what your screens will show by creating a display
                    configuration. Choose a layout that defines how your screen
                    is divided (header, footer, and zones in the main area). A
                    zone is an area on the screen that displays either event
                    schedules or playlist content. Select a theme for colors and
                    fonts, pick which event schedules to display, and assign
                    playlists to specific zones. This is your virtual channel
                    that screens will connect to.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="filled">
                  <Text size="sm" fw={600}>
                    6
                  </Text>
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Connect Your Screens
                  </Text>
                  <Text size="sm" c="dimmed">
                    Finally, connect your physical displays to show your
                    configuration. Open any web browser on your TV or screen
                    device, navigate to the connection URL, scan the QR code
                    with your phone, and select which display configuration to
                    show. The screen will immediately start displaying your
                    content and will automatically update whenever you make
                    changes.
                  </Text>
                </Box>
              </Group>
            </Stack>
          </Stack>
        </Card>

        {/* Advanced Features Section */}
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="lg">
            <Title order={2}>Advanced Features</Title>
            <Divider />

            <Stack gap="md">
              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="violet">
                  <IconLayout size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Custom Display Layouts
                  </Text>
                  <Text size="sm" c="dimmed">
                    Create custom layouts with configurable header, footer, and
                    vertical zones in the main section. Each zone can have a
                    custom width percentage.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="grape">
                  <IconBolt size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Custom Themes
                  </Text>
                  <Text size="sm" c="dimmed">
                    Design unique visual themes with custom fonts and colors for
                    header, main section, event states, and footer.
                  </Text>
                </Box>
              </Group>

              <Group gap="md" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="teal">
                  <IconDeviceTv size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} mb="xs">
                    Embedding Endpoints
                  </Text>
                  <Text size="sm" c="dimmed">
                    Create embeddable event lists or calendars with custom
                    filters, templates, and CSS for integration into external
                    websites.
                  </Text>
                </Box>
              </Group>
            </Stack>
          </Stack>
        </Card>

        {/* Connect Screen Section */}
        <Card shadow="sm" padding="xl" radius="md" withBorder bg="blue.0">
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon size="xl" radius="md" variant="filled">
                <IconDeviceTv size={24} />
              </ThemeIcon>
              <Box>
                <Title order={3}>How to Connect a Screen</Title>
                <Text size="sm" c="dimmed">
                  Works with any TV or device with a web browser
                </Text>
              </Box>
            </Group>

            <Divider />

            <Stack gap="sm">
              <Group gap="sm" align="flex-start">
                <Text fw={600} c="blue.6">
                  1.
                </Text>
                <Text size="sm" style={{ flex: 1 }}>
                  Open a web browser on your display device
                </Text>
              </Group>

              <Group gap="sm" align="flex-start">
                <Text fw={600} c="blue.6">
                  2.
                </Text>
                <Text size="sm" style={{ flex: 1 }}>
                  Navigate to{" "}
                  <Text span fw={600} c="blue.6">
                    https://app.venueboards.grassequal.com/tv
                  </Text>
                </Text>
              </Group>

              <Group gap="sm" align="flex-start">
                <Text fw={600} c="blue.6">
                  3.
                </Text>
                <Text size="sm" style={{ flex: 1 }}>
                  Use your phone to scan the QR code displayed on the screen
                </Text>
              </Group>

              <Group gap="sm" align="flex-start">
                <Text fw={600} c="blue.6">
                  4.
                </Text>
                <Text size="sm" style={{ flex: 1 }}>
                  Sign in to the app and select which Display Configuration to
                  show
                </Text>
              </Group>
            </Stack>

            <Divider />

            <Text size="sm" c="dimmed">
              Multiple screens can show the same configuration simultaneously,
              making it easy to deploy throughout your facility.
            </Text>
          </Stack>
        </Card>
      </Stack>
    </DashboardLayout>
  );
};
